import { Box, Stack } from "@mui/material";
import { Custom, Icon } from "components";
import { useApp, useTheme } from "contexts";
import { ProjectType } from "types";

// icons
import { InsertLinkRounded } from "@mui/icons-material";
import { EnumTheme } from "utils/enums";

type Props = {
  item: ProjectType;
  index?: number;
};

export const Project = (props: Props) => {
  const { theme } = useTheme();
  const { locale, t } = useApp();
  const isDark = theme.id === EnumTheme.Dark;

  const projectIndex = typeof props.index === "number" ? props.index + 1 : 1;
  const indexStr = projectIndex < 10 ? `0${projectIndex}` : `${projectIndex}`;

  return (
    <Box
      aria-label={props.item.name}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: theme.spacing.lg,
        backgroundColor: theme.color.background.accent,
        border: `1px solid ${theme.color.border.color}`,
        borderRadius: theme.border.square,
        boxShadow: `2px 2px 0px 0px ${theme.color.border.color}`,
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translate(-2px, -2px)",
          boxShadow: `4px 4px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
        },
        "&:active": {
          transform: "translate(2px, 2px)",
          boxShadow: "none",
        },
      }}
    >
      <Box>
        {/* Project Header Tag & Platform */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: theme.spacing.sm,
            fontFamily: '"Space Mono", monospace',
            fontSize: theme.font.xxxs,
            fontWeight: theme.font.bold,
            letterSpacing: "0.08em",
            color: theme.color.primary.color,
          }}
        >
          <span>{t.sections.project_badge.split("//")[0].trim()} {"//"} {indexStr}</span>
          <Box
            component="span"
            sx={{
              px: theme.spacing.xs,
              py: theme.spacing.xxs / 2,
              backgroundColor: theme.color.theme.color,
              color: theme.color.font.accent,
              border: `1px solid ${theme.color.border.color}`,
              borderRadius: theme.border.square,
              textTransform: "lowercase",
            }}
          >
            {props.item.platform?.[0] ?? "web"}
          </Box>
        </Box>

        {/* Thumbnail Image if available */}
        {props.item.src && (
          <Box
            sx={{
              mb: theme.spacing.md,
              border: `1px solid ${theme.color.border.color}`,
              borderRadius: theme.border.square,
              overflow: "hidden",
              backgroundColor: theme.color.theme.color,
              maxHeight: 160,
            }}
          >
            <Box
              component="img"
              alt={props.item.name}
              src={`images/${props.item.src}`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        )}

        {/* Title */}
        <Custom.Typography
          variant="h3"
          weight={theme.font.bold}
          sx={{ fontSize: theme.font.md, mb: theme.spacing.xs }}
        >
          {props.item.name}
        </Custom.Typography>

        {/* Description */}
        <Custom.Typography
          variant="body2"
          sx={{
            color: theme.color.font.accent,
            lineHeight: 1.6,
            mb: theme.spacing.md,
            fontSize: theme.font.xs + 1,
          }}
        >
          {props.item.briefdescription[locale.toLowerCase()]}
        </Custom.Typography>

        {/* Tech Stack Icons */}
        {props.item.stack && props.item.stack.length > 0 && (
          <Stack direction="row" spacing={theme.spacing.xs} sx={{ mb: theme.spacing.md, flexWrap: "wrap", gap: 0.5 }}>
            {props.item.stack.map((item: string) => (
              <Box key={item} title={item} sx={{ display: "inline-flex", opacity: 0.85 }}>
                <Icon.Svg name={item} size={18} />
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Links Footer */}
      <Box
        sx={{
          pt: theme.spacing.sm,
          borderTop: `1px solid ${theme.color.border.color}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: '"Space Mono", monospace',
          fontSize: theme.font.xxs,
          fontWeight: theme.font.bold,
        }}
      >
        {props.item.href ? (
          <Box
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            href={props.item.href}
            sx={{
              color: theme.color.primary.color,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <InsertLinkRounded sx={{ fontSize: 16 }} />
            <span>{t.label.live_preview}</span>
          </Box>
        ) : (
          <Box sx={{ color: theme.color.font.accent, opacity: 0.5 }}>--</Box>
        )}

        {props.item.repo ? (
          <Box
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            href={props.item.repo}
            sx={{
              color: theme.color.font.accent,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": { color: theme.color.font.color, textDecoration: "underline" },
            }}
          >
            <Icon.Svg name="github" size={14} fill={theme.color.font.color} />
            <span>{t.label.view_code}</span>
          </Box>
        ) : (
          <Box sx={{ color: theme.color.font.accent, opacity: 0.5 }}>--</Box>
        )}
      </Box>
    </Box>
  );
};